import { IonToggle, type ToggleCustomEvent } from '@ionic/react';
import { useState } from 'react';

import { type PushSupportState, usePushSubscription } from '~/api/hooks/usePushSubscription';
import { IosInstallSheet } from '~/components/IosInstallSheet';
import { Icon, type RxIconName } from '~/components/ui/Icon';
import { useIosInstallHint } from '~/hooks/useIosInstallHint';

interface StateContent {
  label: string;
  description: string;
  hint?: string;
  toneIcon: 'positive' | 'warn' | 'neutral';
}

const STATE_CONTENT: Record<PushSupportState, StateContent> = {
  'granted-active': {
    label: 'Notificações ativadas',
    description: 'Você recebe um alerta quando o status da nota muda.',
    toneIcon: 'positive',
  },
  'granted-idle': {
    label: 'Notificações pausadas',
    description: 'Permissão concedida, mas sem inscrição ativa.',
    hint: 'Ative o toggle para refazer a inscrição.',
    toneIcon: 'neutral',
  },
  default: {
    label: 'Receber notificações',
    description: 'Alerta no celular quando a nota for autorizada, processada ou der erro.',
    toneIcon: 'neutral',
  },
  denied: {
    label: 'Notificações bloqueadas',
    description: 'O navegador está bloqueando notificações deste site.',
    hint: 'Reabilite nas configurações do navegador para este endereço.',
    toneIcon: 'warn',
  },
  unsupported: {
    label: 'Não disponível',
    description: 'Este navegador não suporta notificações push.',
    hint: 'No iPhone, instale o app na tela inicial pra receber alertas.',
    toneIcon: 'warn',
  },
};

const TONE_BG: Record<StateContent['toneIcon'], string> = {
  positive: 'var(--rx-positive-tint)',
  warn: 'var(--rx-warn-tint)',
  neutral: 'var(--rx-brand-tint)',
};

const TONE_FG: Record<StateContent['toneIcon'], string> = {
  positive: 'var(--rx-positive)',
  warn: 'var(--rx-warn)',
  neutral: 'var(--rx-brand)',
};

const TONE_ICON: Record<StateContent['toneIcon'], RxIconName> = {
  positive: 'check',
  warn: 'warn',
  neutral: 'bell',
};

export function NotificationsCard(): JSX.Element {
  const push = usePushSubscription();
  const iosHint = useIosInstallHint();
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const content = STATE_CONTENT[push.state];

  const toggleEnabled = push.state !== 'denied' && push.state !== 'unsupported';
  const isOn = push.state === 'granted-active';
  // Botão "Como instalar?" só aparece quando o problema é a falta de PWA no iOS:
  // estado unsupported + dispositivo iOS + fora de standalone.
  const showIosCta = push.state === 'unsupported' && iosHint.canShowHint;

  const handleToggle = async (event: ToggleCustomEvent): Promise<void> => {
    const next = event.detail.checked;
    if (next === isOn) return;
    if (next) {
      await push.enable();
    } else {
      await push.disable();
    }
  };

  const handleDismissIosSheet = (mode: 'remind-later' | 'installed'): void => {
    iosHint.dismiss(mode);
    setIosSheetOpen(false);
  };

  return (
    <div
      style={{
        background: 'var(--rx-surface)',
        border: '0.5px solid var(--rx-border)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 14px',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: TONE_BG[content.toneIcon],
            color: TONE_FG[content.toneIcon],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={TONE_ICON[content.toneIcon]} size={18} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--rx-text)',
              letterSpacing: '-0.1px',
            }}
          >
            {content.label}
          </div>
          <div
            style={{
              marginTop: 2,
              fontSize: 12,
              color: 'var(--rx-text-2)',
              lineHeight: 1.4,
            }}
          >
            {content.description}
          </div>
        </div>

        <IonToggle
          className="rx-toggle"
          aria-label="Receber notificações Web Push"
          checked={isOn}
          disabled={!toggleEnabled || push.isBusy}
          onIonChange={handleToggle}
        />
      </div>

      {(content.hint || push.errorMessage) && (
        <div
          style={{
            padding: '10px 14px 12px',
            borderTop: '0.5px solid var(--rx-border)',
            background: 'var(--rx-surface-alt)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <Icon
            name={push.errorMessage ? 'warn' : 'help'}
            size={14}
            color={push.errorMessage ? 'var(--rx-negative)' : 'var(--rx-text-3)'}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 11,
                color: push.errorMessage ? 'var(--rx-negative)' : 'var(--rx-text-2)',
                lineHeight: 1.45,
              }}
            >
              {push.errorMessage ?? content.hint}
            </p>
            {showIosCta && (
              <button
                type="button"
                onClick={() => setIosSheetOpen(true)}
                style={{
                  marginTop: 6,
                  padding: '4px 0',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--rx-brand)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  letterSpacing: '-0.1px',
                }}
              >
                Como instalar? →
              </button>
            )}
          </div>
        </div>
      )}

      <IosInstallSheet isOpen={iosSheetOpen} onDismiss={handleDismissIosSheet} />
    </div>
  );
}
